import React, { useEffect, useState } from "react";
import { Pencil, Check, Eye, EyeOff } from "lucide-react";
import { toast } from "react-hot-toast";
import { permissionAPI } from "../../services/AdminPortal/permissionsAPI";

const ROLES = ["admin", "subAdmin", "credit-manager", "staff"];

const EditEmployeeModal = ({ isOpen, onClose, employee, onUpdated }) => {
  const [formData, setFormData] = useState({});
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (employee) {
      setFormData({
        name: employee.name || "",
        email: employee.email || "",
        phone: employee.phone || "",
        role: employee.role || "",
        status: employee.status || "active",
        location: employee.location || "",
        targetLoans: employee.targetLoans || 0,
        password: "",
      });
    }
  }, [employee]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const updates = { ...formData };

      // ❌ don't send empty password
      if (!updates.password) delete updates.password;

      await permissionAPI.updateEmployee(employee.staffId, updates);

      toast.success("Employee updated successfully");

      onUpdated(); // refresh table
      onClose();
    } catch (err) {
      console.error(err);
      toast.error(err.message || "Update failed");
    }
  };

  if (!isOpen || !employee) return null;

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center">
      <div className="bg-[#131313] text-white p-6 rounded-lg w-full max-w-xl">
        <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
          <Pencil size={16} className="text-yellow-400" />
          Edit Employee
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* NAME */}
          <div>
            <label className="text-sm">Name</label>
            <input
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="w-full p-2 bg-black border border-[#333] rounded"
            />
          </div>

          {/* EMAIL */}
          <div>
            <label className="text-sm">Email</label>
            <input
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="w-full p-2 bg-black border border-[#333] rounded"
            />
          </div>

          {/* PHONE */}
          <div>
            <label className="text-sm">Phone</label>
            <input
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              className="w-full p-2 bg-black border border-[#333] rounded"
            />
          </div>

          {/* ROLE */}
          <div>
            <label className="text-sm">Role</label>
            <select
              name="role"
              value={formData.role}
              onChange={handleChange}
              className="w-full p-2 bg-black border border-[#333] rounded"
            >
              {ROLES.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </div>

          {/* STATUS */}
          <div>
            <label className="text-sm">Status</label>
            <select
              name="status"
              value={formData.status}
              onChange={handleChange}
              className="w-full p-2 bg-black border border-[#333] rounded"
            >
              <option value="active">active</option>
              <option value="inactive">inactive</option>
            </select>
          </div>

          {/* TARGET */}
          <div>
            <label className="text-sm">Target Loans</label>
            <input
              type="number"
              name="targetLoans"
              value={formData.targetLoans}
              onChange={handleChange}
              className="w-full p-2 bg-black border border-[#333] rounded"
            />
          </div>

          {/* PASSWORD */}
          <div className="relative">
            <label className="text-sm">New Password</label>
            <input
              type={showPassword ? "text" : "password"}
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Leave empty if no change"
              className="w-full p-2 bg-black border border-[#333] rounded"
            />

            <button
              type="button"
              onClick={() => setShowPassword((p) => !p)}
              className="absolute right-3 top-8 text-gray-400"
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>

          {/* LOCATION */}
          <div>
            <label className="text-sm">Location</label>
            <input
              name="location"
              value={formData.location}
              onChange={handleChange}
              className="w-full p-2 bg-black border border-[#333] rounded"
            />
          </div>

          {/* ACTIONS */}
          <div className="flex justify-end gap-3 mt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-white rounded"
            >
              Cancel
            </button>

            <button
              type="submit"
              className="px-4 py-2 bg-[#FFCA20] text-black rounded flex items-center gap-2"
            >
              Save <Check size={16} />
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditEmployeeModal;